import { ButtonHTMLAttributes, forwardRef } from 'react';
import { LucideIcon } from 'lucide-react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'default' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant = 'default', size = 'md', icon: Icon, loading, className = '', disabled, ...props }, ref) => {
    const variants = {
      primary: 'bg-primary text-white border-primary hover:bg-primary-hover',
      default: 'bg-white border-slate-300 text-slate-800 hover:border-slate-500 hover:bg-slate-50',
      ghost: 'bg-transparent border-transparent text-slate-600 hover:bg-slate-100',
      danger: 'bg-white border-danger/30 text-danger hover:bg-danger-bg',
    };
    const sizes = {
      sm: 'text-xs px-2.5 py-1.5',
      md: 'text-[13px] px-3.5 py-2',
      lg: 'text-sm px-4 py-2.5',
    };

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={`inline-flex items-center gap-2 border rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
        {...props}
      >
        {Icon && !loading && <Icon size={size === 'sm' ? 13 : 15} strokeWidth={2} />}
        {loading && (
          <svg className="animate-spin" width={14} height={14} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
            <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
          </svg>
        )}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';
